import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImageIcon } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { useIntegratorMe, isIntegratorAdmin } from "@/lib/me-query";
import {
  fetchWorkspaceAvatarSyncStatus,
  startWorkspaceOAuth,
  syncAllWorkspaceAvatars,
  WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL,
} from "@/lib/workspaceAvatarSync";

const bannerActionClassName =
  "font-medium text-amber-900 underline decoration-amber-500 underline-offset-2 hover:text-amber-950 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60";

function formatBannerActionError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Request failed. Try again or open the directory.";
}

export function WorkspaceAvatarMissingBanner() {
  const location = useLocation();
  const meQuery = useIntegratorMe();
  const operatorSession =
    isIntegratorAdmin(meQuery.data?.user) &&
    (meQuery.data?.user.email?.trim().toLowerCase() ?? "") === WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL;
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const statusQuery = useQuery({
    queryKey: ["workspace-avatar-sync", "status"],
    queryFn: fetchWorkspaceAvatarSyncStatus,
    enabled: operatorSession && meQuery.isSuccess,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const connectMutation = useMutation({
    mutationFn: () =>
      startWorkspaceOAuth({
        returnTo: `${location.pathname}${location.search}${location.hash}`,
        uiOrigin: window.location.origin,
      }),
    onMutate: () => {
      setActionError(null);
    },
    onSuccess: (payload) => {
      window.location.assign(payload.authorizeUrl);
    },
    onError: (error) => {
      setActionError(formatBannerActionError(error));
    },
  });

  const syncMutation = useMutation({
    mutationFn: syncAllWorkspaceAvatars,
    onMutate: () => {
      setActionError(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["workspace-avatar-sync", "status"] });
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "directory"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (error) => {
      setActionError(formatBannerActionError(error));
    },
  });

  const status = statusQuery.data;
  if (!status?.operatorEligible || status.avatarSyncPendingCount === 0) {
    return null;
  }

  const needsConnect = !status.workspaceConnected;
  const count = status.avatarSyncPendingCount;
  const bannerMessage = needsConnect
    ? `${count} hub ${count === 1 ? "user has" : "users have"} no profile photo because Workspace avatar sync has not run yet.`
    : `${count} hub ${count === 1 ? "user has" : "users have"} no profile photo. Avatar sync can refresh photos from Google Workspace.`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative z-[40] border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950"
    >
      <div className="mx-auto flex w-full max-w-[min(100%,2400px)] flex-wrap items-center justify-center gap-x-3 gap-y-2 px-3 sm:px-4 lg:px-6 2xl:px-10">
        <ImageIcon className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
        <span>{bannerMessage}</span>
        {needsConnect ? (
          status.oauthConfigured ? (
            <button
              type="button"
              className={bannerActionClassName}
              disabled={connectMutation.isPending}
              onClick={() => void connectMutation.mutate()}
            >
              {connectMutation.isPending ? "Connecting…" : "Connect Google Workspace"}
            </button>
          ) : (
            <span className="font-medium text-amber-900">
              Google Workspace sign-in is not configured on this hub.
            </span>
          )
        ) : (
          <button
            type="button"
            className={bannerActionClassName}
            disabled={syncMutation.isPending}
            onClick={() => void syncMutation.mutate()}
          >
            {syncMutation.isPending ? "Refreshing…" : "Refresh all avatars"}
          </button>
        )}
        <Link to="/admin/hub-accounts" className={bannerActionClassName}>
          Open directory
        </Link>
        {actionError != null ? (
          <span className="basis-full text-center text-amber-900">{actionError}</span>
        ) : null}
      </div>
    </div>
  );
}
